import { UiLocale, resolveUiLocale } from "./locale";

export interface UiCopy {
  productTitle: string;
  viewJobDescription: string;
  logoAlt: string;
  steps: { verification: string; interview: string; analysis: string };
  generalQuestions: string;
  personalizedQuestions: string;
  progressOf: (current: number, total: number) => string;
  questionOf: (current: number, total: number) => string;
  yes: string;
  no: string;
  initializing: string;
  validatingApplicationId: string;
  preparingInterview: string;
  preparingInterviewPlaceholder: string;
  preparingRole: (jobTitle: string) => string;
  enterApplicationId: string;
  yourResponse: string;
  send: string;
  complete: string;
  validating: string;
  unavailable: string;
  retry: string;
  errorOccurred: string;
  missingApplicationId: string;
  applicationIdMissingPlaceholder: string;
  validationFailed: (detail: string) => string;
  validationFailedConnection: string;
  noDescription: string;
  position: string;
  jobDescriptionTitle: string;
  connectionLost: (attempt: number) => string;
  connectionFailed: string;
  thinking: string;
  processingResponse: string;
  generatingAnalysis: string;
  pleaseWait: string;
  referralPrompt: string;
  feedbackTitle: string;
  feedbackTooltip: string;
  feedbackPlaceholder: string;
  feedbackInputLabel: string;
  feedbackPrompt: string;
  ratingLabels: readonly string[];
  submit: string;
  submitted: string;
  thanksFeedback: string;
  submitAnswer: string;
  interviewResponseInput: string;
  recording: string;
  processing: string;
  processingSpeech: string;
  startVoice: string;
  stopVoice: string;
  clickToStopRecording: string;
  clickToStartVoice: string;
  voiceUnsupported: string;
  voiceUnsupportedTitle: string;
  enableTts: string;
  disableTts: string;
  processingAudio: string;
  clickToEnableTts: string;
  clickToDisableTts: string;
  referFriend: string;
  contactName: string;
  contactEmail: string;
  phoneOptional: string;
  companyOptional: string;
  skip: string;
  generateLinks: string;
  preparingShort: string;
  warmIntroduction: string;
  recommended: string;
  warmIntroSubtitle: string;
  referralSteps: string;
  stepsLabel: string;
  chooseHow: string;
  reviewNote: string;
  sendIntro: string;
  chooseChannel: string;
  sendEmail: string;
  sendText: string;
  moreEmailOptions: string;
  moreTextOptions: string;
  gmailWeb: string;
  outlookWeb: string;
  defaultEmail: string;
  channelHint: string;
  orShareLink: string;
  personalReferralLink: string;
  copyReferralLink: string;
  copyLink: string;
  copied: string;
  openLandingPage: string;
  openReferralPage: string;
  showLink: string;
  referralNotice: string;
  linksFailed: string;
  introThanks: string;
  introStatusFailed: string;
  copyFailed: string;
  salarySubmitted: (value: string) => string;
  defaultPhoneCountry: "US" | "MX" | "BR";
  interviewErrorFallback: string;
}

const progress = (current: number, total: number): string => `${current} de ${total}`;

const ENGLISH: UiCopy = {
  productTitle: "ISA - Interview Smart Agent",
  viewJobDescription: "View JD 📋",
  logoAlt: "Theia Logo",
  steps: { verification: "Verification", interview: "Interview", analysis: "Analysis" },
  generalQuestions: "General Questions",
  personalizedQuestions: "Personalized Questions",
  progressOf: (current, total) => `${current} of ${total}`,
  questionOf: (current, total) => `Question ${current} of ${total}`,
  yes: "Yes",
  no: "No",
  initializing: "Initializing...",
  validatingApplicationId: "Validating Application ID...",
  preparingInterview: "Preparing Interview...",
  preparingInterviewPlaceholder: "Preparing interview...",
  preparingRole: (jobTitle) => `Preparing your interview for the ${jobTitle} role...`,
  enterApplicationId: "Enter Application ID...",
  yourResponse: "Your response...",
  send: "Send",
  complete: "Complete",
  validating: "Validating...",
  unavailable: "Unavailable",
  retry: "Retry",
  errorOccurred: "Error occurred...",
  missingApplicationId:
    "<strong>Error:</strong> Missing Application ID. Please use the link provided in the email. If the issue persists, contact the recruiting team.",
  applicationIdMissingPlaceholder: "Application ID missing from URL",
  validationFailed: (detail) => `❌ ${detail}`,
  validationFailedConnection:
    "❌ Failed to validate Application ID. Please check your connection and try again.",
  noDescription: "No description available.",
  position: "Position",
  jobDescriptionTitle: "Job Description",
  connectionLost: (attempt) =>
    `Connection lost. Attempting to reconnect... (Attempt ${attempt})`,
  connectionFailed: "Could not reconnect to the server. Please refresh the page to try again.",
  thinking: "ISA is thinking...",
  processingResponse: "Processing your response...",
  generatingAnalysis: "Generating analysis...",
  pleaseWait: "Please wait.",
  referralPrompt:
    "Do you know someone hiring or growing their company that might benefit from having ISA's 24/7 help? If so, please share their details below.",
  feedbackTitle: "Share your interview experience",
  feedbackTooltip:
    "Share any thoughts about your interview experience (pacing, clarity, issues). This won’t affect your application.",
  feedbackPlaceholder: "Share your feedback (optional)",
  feedbackInputLabel: "Feedback response input",
  feedbackPrompt: "How satisfied were you with your interview experience?",
  ratingLabels: [
    "Very dissatisfied",
    "Dissatisfied",
    "Neither satisfied nor dissatisfied",
    "Satisfied",
    "Very satisfied",
  ],
  submit: "Submit",
  submitted: "Submitted",
  thanksFeedback: "Thanks for your feedback.",
  submitAnswer: "Submit Answer",
  interviewResponseInput: "Interview response input",
  recording: "Recording...",
  processing: "Processing...",
  processingSpeech: "Processing speech...",
  startVoice: "Start voice input",
  stopVoice: "Stop voice input",
  clickToStopRecording: "Click to stop recording",
  clickToStartVoice: "Click to start voice input",
  voiceUnsupported: "Voice input not supported",
  voiceUnsupportedTitle: "Speech recognition not supported in this browser",
  enableTts: "Enable TTS audio",
  disableTts: "Disable TTS audio",
  processingAudio: "Processing audio",
  clickToEnableTts: "Click to enable TTS audio",
  clickToDisableTts: "Click to disable TTS audio",
  referFriend: "Refer a Friend",
  contactName: "Contact's name",
  contactEmail: "Contact's email",
  phoneOptional: "Phone Number (Optional)",
  companyOptional: "Company (Optional)",
  skip: "Skip",
  generateLinks: "Generate Intro Links",
  preparingShort: "Preparing…",
  warmIntroduction: "Warm Introduction",
  recommended: "Recommended",
  warmIntroSubtitle: "A friendly intro from you lands in the inbox and converts best.",
  referralSteps: "Referral Steps",
  stepsLabel: "Steps",
  chooseHow: "Choose how to introduce",
  reviewNote: "Review the note",
  sendIntro: "Send the intro",
  chooseChannel: "Choose a channel",
  sendEmail: "Send Email",
  sendText: "Send Text",
  moreEmailOptions: "More email options",
  moreTextOptions: "More text options",
  gmailWeb: "Gmail (web)",
  outlookWeb: "Outlook (web)",
  defaultEmail: "Default email",
  channelHint: "We’ll open a short, pre‑filled note in your selected app. You can edit before sending.",
  orShareLink: "Or share a link",
  personalReferralLink: "Your personal referral link",
  copyReferralLink: "Copy referral link",
  copyLink: "Copy link",
  copied: "Copied",
  openLandingPage: "Open landing page",
  openReferralPage: "Open referral page",
  showLink: "Show link",
  referralNotice: "Your friend will only hear from us if you send the intro — you’re in control.",
  linksFailed: "Failed to generate links",
  introThanks: "Thanks — your warm intro helps ensure deliverability.",
  introStatusFailed: "Could not record the intro status. You can continue anyway.",
  copyFailed: "Copy failed",
  salarySubmitted: (value) => `Salary info submitted: ${value}`,
  defaultPhoneCountry: "US",
  interviewErrorFallback: "An error occurred while preparing the interview. Please try again later.",
};

const SPANISH: UiCopy = {
  productTitle: "ISA - Agente Inteligente de Entrevistas",
  viewJobDescription: "Ver descripción 📋",
  logoAlt: "Logotipo de Theia",
  steps: { verification: "Verificación", interview: "Entrevista", analysis: "Análisis" },
  generalQuestions: "Preguntas generales",
  personalizedQuestions: "Preguntas personalizadas",
  progressOf: progress,
  questionOf: (current, total) => `Pregunta ${current} de ${total}`,
  yes: "Sí",
  no: "No",
  initializing: "Iniciando...",
  validatingApplicationId: "Validando el ID de la postulación...",
  preparingInterview: "Preparando la entrevista...",
  preparingInterviewPlaceholder: "Preparando la entrevista...",
  preparingRole: (jobTitle) => `Preparando tu entrevista para el puesto de ${jobTitle}...`,
  enterApplicationId: "Ingresa el ID de la postulación...",
  yourResponse: "Tu respuesta...",
  send: "Enviar",
  complete: "Completar",
  validating: "Validando...",
  unavailable: "No disponible",
  retry: "Reintentar",
  errorOccurred: "Ocurrió un error...",
  missingApplicationId:
    "<strong>Error:</strong> Falta el ID de la postulación. Usa el enlace del correo. Si el problema continúa, contacta al equipo de reclutamiento.",
  applicationIdMissingPlaceholder: "Falta el ID de la postulación en el enlace",
  validationFailed: (detail) => `❌ ${detail}`,
  validationFailedConnection:
    "❌ No se pudo validar el ID de la postulación. Revisa tu conexión e inténtalo de nuevo.",
  noDescription: "No hay descripción disponible.",
  position: "Puesto",
  jobDescriptionTitle: "Descripción del puesto",
  connectionLost: (attempt) =>
    `Se perdió la conexión. Intentando reconectar... (Intento ${attempt})`,
  connectionFailed:
    "No pudimos reconectar con el servidor. Actualiza la página para intentarlo de nuevo.",
  thinking: "ISA está pensando...",
  processingResponse: "Procesando tu respuesta...",
  generatingAnalysis: "Generando el análisis...",
  pleaseWait: "Espera un momento.",
  referralPrompt:
    "¿Conoces a alguien que esté contratando o haciendo crecer su empresa y que pueda beneficiarse de la ayuda de ISA las 24 horas? Si es así, comparte sus datos abajo.",
  feedbackTitle: "Comparte tu experiencia en la entrevista",
  feedbackTooltip:
    "Cuéntanos qué te pareció la entrevista (ritmo, claridad, problemas). Esto no afecta tu postulación.",
  feedbackPlaceholder: "Comparte tus comentarios (opcional)",
  feedbackInputLabel: "Comentarios sobre la entrevista",
  feedbackPrompt: "¿Qué tan satisfecho estás con tu experiencia en la entrevista?",
  ratingLabels: [
    "Muy insatisfecho",
    "Insatisfecho",
    "Ni satisfecho ni insatisfecho",
    "Satisfecho",
    "Muy satisfecho",
  ],
  submit: "Enviar",
  submitted: "Enviado",
  thanksFeedback: "Gracias por tus comentarios.",
  submitAnswer: "Enviar respuesta",
  interviewResponseInput: "Respuesta de la entrevista",
  recording: "Grabando...",
  processing: "Procesando...",
  processingSpeech: "Procesando la voz...",
  startVoice: "Iniciar entrada de voz",
  stopVoice: "Detener entrada de voz",
  clickToStopRecording: "Haz clic para detener la grabación",
  clickToStartVoice: "Haz clic para iniciar la entrada de voz",
  voiceUnsupported: "Entrada de voz no disponible",
  voiceUnsupportedTitle: "Este navegador no admite reconocimiento de voz",
  enableTts: "Activar el audio",
  disableTts: "Desactivar el audio",
  processingAudio: "Procesando el audio",
  clickToEnableTts: "Haz clic para activar el audio",
  clickToDisableTts: "Haz clic para desactivar el audio",
  referFriend: "Recomienda a alguien",
  contactName: "Nombre del contacto",
  contactEmail: "Correo del contacto",
  phoneOptional: "Teléfono (opcional)",
  companyOptional: "Empresa (opcional)",
  skip: "Omitir",
  generateLinks: "Generar enlaces",
  preparingShort: "Preparando…",
  warmIntroduction: "Presentación",
  recommended: "Recomendado",
  warmIntroSubtitle: "Una presentación tuya llega mejor a la bandeja de entrada.",
  referralSteps: "Pasos de la recomendación",
  stepsLabel: "Pasos",
  chooseHow: "Elige cómo presentarlo",
  reviewNote: "Revisa la nota",
  sendIntro: "Envía la presentación",
  chooseChannel: "Elige un canal",
  sendEmail: "Enviar correo",
  sendText: "Enviar mensaje",
  moreEmailOptions: "Más opciones de correo",
  moreTextOptions: "Más opciones de mensaje",
  gmailWeb: "Gmail (web)",
  outlookWeb: "Outlook (web)",
  defaultEmail: "Correo predeterminado",
  channelHint:
    "Abriremos una nota breve y prellenada en la app que elijas. Puedes editarla antes de enviarla.",
  orShareLink: "O comparte un enlace",
  personalReferralLink: "Tu enlace personal de recomendación",
  copyReferralLink: "Copiar enlace de recomendación",
  copyLink: "Copiar enlace",
  copied: "Copiado",
  openLandingPage: "Abrir la página",
  openReferralPage: "Abrir página de recomendación",
  showLink: "Mostrar enlace",
  referralNotice:
    "Tu contacto solo recibirá noticias nuestras si envías la presentación. Tú tienes el control.",
  linksFailed: "No se pudieron generar los enlaces",
  introThanks: "Gracias. Tu presentación ayuda a que el mensaje llegue.",
  introStatusFailed: "No pudimos registrar el envío. Puedes continuar de todas formas.",
  copyFailed: "No se pudo copiar",
  salarySubmitted: (value) => `Información salarial enviada: ${value}`,
  defaultPhoneCountry: "MX",
  interviewErrorFallback:
    "Ocurrió un error al preparar la entrevista. Inténtalo de nuevo más tarde.",
};

const PORTUGUESE: UiCopy = {
  productTitle: "ISA - Agente Inteligente de Entrevistas",
  viewJobDescription: "Ver descrição 📋",
  logoAlt: "Logotipo da Theia",
  steps: { verification: "Verificação", interview: "Entrevista", analysis: "Análise" },
  generalQuestions: "Perguntas gerais",
  personalizedQuestions: "Perguntas personalizadas",
  progressOf: progress,
  questionOf: (current, total) => `Pergunta ${current} de ${total}`,
  yes: "Sim",
  no: "Não",
  initializing: "Iniciando...",
  validatingApplicationId: "Validando o ID da candidatura...",
  preparingInterview: "Preparando a entrevista...",
  preparingInterviewPlaceholder: "Preparando a entrevista...",
  preparingRole: (jobTitle) => `Preparando sua entrevista para a vaga de ${jobTitle}...`,
  enterApplicationId: "Informe o ID da candidatura...",
  yourResponse: "Sua resposta...",
  send: "Enviar",
  complete: "Concluir",
  validating: "Validando...",
  unavailable: "Indisponível",
  retry: "Tentar de novo",
  errorOccurred: "Ocorreu um erro...",
  missingApplicationId:
    "<strong>Erro:</strong> O ID da candidatura está ausente. Use o link enviado por e-mail. Se o problema continuar, fale com a equipe de recrutamento.",
  applicationIdMissingPlaceholder: "ID da candidatura ausente no link",
  validationFailed: (detail) => `❌ ${detail}`,
  validationFailedConnection:
    "❌ Não foi possível validar o ID da candidatura. Verifique sua conexão e tente de novo.",
  noDescription: "Nenhuma descrição disponível.",
  position: "Vaga",
  jobDescriptionTitle: "Descrição da vaga",
  connectionLost: (attempt) =>
    `Conexão perdida. Tentando reconectar... (Tentativa ${attempt})`,
  connectionFailed:
    "Não foi possível reconectar ao servidor. Atualize a página para tentar de novo.",
  thinking: "A ISA está pensando...",
  processingResponse: "Processando sua resposta...",
  generatingAnalysis: "Gerando a análise...",
  pleaseWait: "Aguarde um momento.",
  referralPrompt:
    "Você conhece alguém que esteja contratando ou fazendo a empresa crescer e que possa se beneficiar da ajuda da ISA 24 horas? Se sim, compartilhe os dados abaixo.",
  feedbackTitle: "Conte como foi sua entrevista",
  feedbackTooltip:
    "Compartilhe o que achou da entrevista (ritmo, clareza, problemas). Isso não afeta sua candidatura.",
  feedbackPlaceholder: "Compartilhe seu feedback (opcional)",
  feedbackInputLabel: "Feedback da entrevista",
  feedbackPrompt: "Quão satisfeito você ficou com sua experiência na entrevista?",
  ratingLabels: [
    "Muito insatisfeito",
    "Insatisfeito",
    "Nem satisfeito nem insatisfeito",
    "Satisfeito",
    "Muito satisfeito",
  ],
  submit: "Enviar",
  submitted: "Enviado",
  thanksFeedback: "Obrigado pelo seu feedback.",
  submitAnswer: "Enviar resposta",
  interviewResponseInput: "Resposta da entrevista",
  recording: "Gravando...",
  processing: "Processando...",
  processingSpeech: "Processando a fala...",
  startVoice: "Iniciar entrada de voz",
  stopVoice: "Parar entrada de voz",
  clickToStopRecording: "Clique para parar a gravação",
  clickToStartVoice: "Clique para iniciar a entrada de voz",
  voiceUnsupported: "Entrada de voz indisponível",
  voiceUnsupportedTitle: "Este navegador não oferece reconhecimento de fala",
  enableTts: "Ativar o áudio",
  disableTts: "Desativar o áudio",
  processingAudio: "Processando o áudio",
  clickToEnableTts: "Clique para ativar o áudio",
  clickToDisableTts: "Clique para desativar o áudio",
  referFriend: "Indique alguém",
  contactName: "Nome do contato",
  contactEmail: "E-mail do contato",
  phoneOptional: "Telefone (opcional)",
  companyOptional: "Empresa (opcional)",
  skip: "Pular",
  generateLinks: "Gerar links",
  preparingShort: "Preparando…",
  warmIntroduction: "Apresentação",
  recommended: "Recomendado",
  warmIntroSubtitle: "Uma apresentação sua chega melhor à caixa de entrada.",
  referralSteps: "Etapas da indicação",
  stepsLabel: "Etapas",
  chooseHow: "Escolha como apresentar",
  reviewNote: "Revise a mensagem",
  sendIntro: "Envie a apresentação",
  chooseChannel: "Escolha um canal",
  sendEmail: "Enviar e-mail",
  sendText: "Enviar mensagem",
  moreEmailOptions: "Mais opções de e-mail",
  moreTextOptions: "Mais opções de mensagem",
  gmailWeb: "Gmail (web)",
  outlookWeb: "Outlook (web)",
  defaultEmail: "E-mail padrão",
  channelHint:
    "Vamos abrir uma mensagem curta e preenchida no app escolhido. Você pode editar antes de enviar.",
  orShareLink: "Ou compartilhe um link",
  personalReferralLink: "Seu link pessoal de indicação",
  copyReferralLink: "Copiar link de indicação",
  copyLink: "Copiar link",
  copied: "Copiado",
  openLandingPage: "Abrir a página",
  openReferralPage: "Abrir página de indicação",
  showLink: "Mostrar link",
  referralNotice:
    "Seu contato só vai receber notícias nossas se você enviar a apresentação. Você decide.",
  linksFailed: "Não foi possível gerar os links",
  introThanks: "Obrigado. Sua apresentação ajuda a mensagem a chegar.",
  introStatusFailed: "Não conseguimos registrar o envio. Você pode continuar mesmo assim.",
  copyFailed: "Não foi possível copiar",
  salarySubmitted: (value) => `Informação salarial enviada: ${value}`,
  defaultPhoneCountry: "BR",
  interviewErrorFallback:
    "Ocorreu um erro ao preparar a entrevista. Tente de novo mais tarde.",
};

const COPY: Record<UiLocale, UiCopy> = {
  en: ENGLISH,
  es: SPANISH,
  pt: PORTUGUESE,
};

export const getUiCopy = (language?: string | null): UiCopy => {
  const locale: UiLocale =
    language === "en" || language === "es" || language === "pt" ? language : resolveUiLocale(language);
  return COPY[locale];
};

const FRONTEND_STATUS_KEYS = [
  "initializing",
  "validatingApplicationId",
  "preparingInterview",
  "preparingInterviewPlaceholder",
  "interviewErrorFallback",
] as const;

export const localizeFrontendStatus = (text: string, language?: string | null): string => {
  const trimmed = text.trim();
  for (const key of FRONTEND_STATUS_KEYS) {
    for (const source of ["en", "es", "pt"] as UiLocale[]) {
      if (COPY[source][key] === trimmed) {
        return getUiCopy(language)[key];
      }
    }
  }
  return text;
};

export const isStartupOverlayMessage = (message?: string | null): boolean => {
  const value = (message || "").toLowerCase();
  if (!value) {
    return false;
  }
  return (
    value.includes("initializing") ||
    value.includes("iniciando") ||
    value.includes("validating") ||
    value.includes("validando") ||
    value.includes("preparing") ||
    value.includes("preparando")
  );
};

export type PlatformStatusKind = "thinking" | "processing" | "analysis";

export const platformStatusKind = (message?: string | null): PlatformStatusKind | null => {
  const value = (message || "").toLowerCase();
  if (!value) {
    return null;
  }
  if (value.includes("isa is thinking") || value.includes("isa está pensando") || value.includes("a isa está pensando")) {
    return "thinking";
  }
  if (
    value.includes("generating") ||
    value.includes("analysis") ||
    value.includes("generando") ||
    value.includes("análisis") ||
    value.includes("analise") ||
    value.includes("análise") ||
    value.includes("gerando")
  ) {
    return "analysis";
  }
  if (
    value.includes("processing") ||
    value.includes("preparing") ||
    value.includes("procesando") ||
    value.includes("preparando") ||
    value.includes("processando")
  ) {
    return "processing";
  }
  return null;
};
